/**
 * Buffer de diagnóstico que viaja como `error_log` en los reportes de bug.
 *
 * Este módulo no importa nada a propósito: lo consumen `@/lib/axios` y
 * `@/router`, que están cerca de la raíz del grafo de imports, y cualquier
 * dependencia aquí crearía un ciclo.
 */

const MAX_ENTRIES = 30

/** Tope por entrada, para que un mensaje de backend enorme no coma el buffer. */
const MAX_MESSAGE_CHARS = 300

const buffer: string[] = []
let patched = false

function timestamp(): string {
	return new Date().toISOString()
}

function truncate(value: string, max = MAX_MESSAGE_CHARS): string {
	return value.length > max ? `${value.slice(0, max)}… (+${value.length - max} chars)` : value
}

function formatArgs(args: unknown[]): string {
	return args
		.map((arg) => {
			if (arg instanceof Error) {
				return `${arg.name}: ${arg.message}\n${arg.stack ?? ''}`
			}
			if (typeof arg === 'object' && arg !== null) {
				try {
					return JSON.stringify(arg)
				} catch {
					return String(arg)
				}
			}
			return String(arg)
		})
		.join(' ')
}

function push(entry: string) {
	buffer.push(entry)
	if (buffer.length > MAX_ENTRIES) {
		buffer.shift()
	}
}

function patchConsole() {
	if (patched) return
	patched = true

	const originalError = console.error.bind(console)
	console.error = (...args: unknown[]) => {
		push(`[${timestamp()}] console.error: ${formatArgs(args)}`)
		originalError(...args)
	}

	window.addEventListener('error', (event) => {
		push(`[${timestamp()}] Uncaught: ${event.message}`)
	})

	window.addEventListener('unhandledrejection', (event) => {
		push(`[${timestamp()}] UnhandledRejection: ${formatArgs([event.reason])}`)
	})
}

export interface HttpFailure {
	method?: string
	/** Path sin query string. El query puede traer datos personales. */
	path: string
	/** 0 cuando no hubo respuesta (timeout, red caída, CORS). */
	status: number
	durationMs?: number
	/** Mensaje del backend o código de axios. Nunca el body de la petición. */
	message?: string
}

/**
 * Registra una petición fallida. Es lo que convierte un "AxiosError: Request
 * failed with status code 500" —cuyo stack son frames de `axios.js`, inútiles—
 * en algo accionable: qué endpoint, qué método y qué contestó el backend.
 *
 * A propósito NO se registra el body de la petición ni el header Authorization:
 * por aquí pasan tarjetas (Enigma Vault, OpenPay) y datos de viajeros, y este
 * log termina en un bucket que lee personal de soporte.
 */
export function recordHttpFailure(failure: HttpFailure): void {
	const parts = [
		`HTTP ${failure.status || 'sin respuesta'}`,
		`${failure.method?.toUpperCase() ?? '?'} ${failure.path}`,
	]

	if (typeof failure.durationMs === 'number') {
		parts.push(`(${Math.round(failure.durationMs)}ms)`)
	}

	if (failure.message) {
		parts.push(`— ${truncate(failure.message)}`)
	}

	push(`[${timestamp()}] ${parts.join(' ')}`)
}

/**
 * Deja constancia de que la captura de pantalla no se pudo adjuntar. Sin esto,
 * un reporte sin imagen es indistinguible de uno donde la captura falló.
 */
export function recordCaptureFailure(reason: string): void {
	push(`[${timestamp()}] captura de pantalla no adjuntada: ${truncate(reason)}`)
}

/**
 * Avisos de la captura que no impiden adjuntarla, pero que alguien debe ver: hoy
 * solo el caso en que el modal de feedback no quedó excluido y por lo tanto sale
 * tapando la imagen.
 */
export function recordCaptureWarning(reason: string): void {
	push(`[${timestamp()}] aviso de captura: ${truncate(reason)}`)
}

/** Rastro de navegación, para saber por dónde pasó el usuario antes de la falla. */
export function recordNavigation(from: string, to: string): void {
	push(`[${timestamp()}] nav: ${from} → ${to}`)
}

/**
 * Contexto del entorno, calculado al momento de enviar el reporte. Sin esto no
 * se puede reproducir: no se sabe en qué navegador, a qué tamaño de pantalla ni
 * contra qué build ocurrió.
 */
function environmentSummary(): string {
	const buildId = import.meta.env.VITE_BUILD_ID
	const lines = [
		`Fecha: ${timestamp()} (${Intl.DateTimeFormat().resolvedOptions().timeZone})`,
		`Build: ${import.meta.env.MODE}${buildId ? ` · ${buildId}` : ''}`,
		`URL: ${window.location.pathname}`,
		`Navegador: ${navigator.userAgent}`,
		`Viewport: ${window.innerWidth}x${window.innerHeight} @${window.devicePixelRatio}x`,
		`Idioma: ${navigator.language}`,
		`Conexión: ${navigator.onLine ? 'online' : 'offline'}`,
	]

	return lines.join('\n')
}

export function useConsoleLogBuffer() {
	patchConsole()

	return {
		getRecentLogs(limit = MAX_ENTRIES): string {
			const entries = buffer.slice(-limit)

			return [
				'=== Entorno ===',
				environmentSummary(),
				'',
				'=== Registro ===',
				entries.length > 0 ? entries.join('\n') : '(sin eventos registrados)',
			].join('\n')
		},
	}
}
